import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Shield, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import EmailInboundConfig from "@/components/EmailInboundConfig";

function TwoFASetup({ user, onChange }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState(null);
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = user?.totp_enabled;

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSecret(data.secret); setQr(data.qr_data_uri); setOpen(true); }
    catch { toast.error("Errore setup 2FA"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    try { await api.post("/auth/2fa/verify", { code }); toast.success("2FA attivata!"); setOpen(false); onChange(); }
    catch { toast.error("Codice non valido"); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable"); toast.success("2FA disattivata"); onChange(); }
    catch { toast.error("Errore"); }
  };

  return (
    <Card className="p-6 space-y-3 max-w-xl" data-testid="2fa-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${enabled ? "bg-emerald-100" : "bg-slate-100"}`}>
            <Shield className={`w-5 h-5 ${enabled ? "text-emerald-700" : "text-slate-500"}`} />
          </div>
          <div>
            <div className="font-heading font-semibold text-navy-900">Autenticazione a due fattori (TOTP)</div>
            <div className="text-xs text-slate-500">{enabled ? "Attiva — il tuo account è protetto" : "Non attiva — consigliata per ruoli sensibili"}</div>
          </div>
        </div>
        {enabled ? (
          <Button variant="outline" className="border-red-200 text-red-600" onClick={disable} data-testid="2fa-disable">Disattiva</Button>
        ) : (
          <Button className="bg-navy-800 hover:bg-navy-900" onClick={startSetup} disabled={busy} data-testid="2fa-setup">Attiva 2FA</Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Configura 2FA</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            <p className="text-sm text-slate-600">Scansiona il QR con Google Authenticator / Authy / Microsoft Authenticator, poi inserisci il codice a 6 cifre.</p>
            {qr && <img src={qr} alt="QR" className="mx-auto w-48 h-48 border border-slate-200 rounded" />}
            {secret && <div className="text-xs text-slate-500 font-mono break-all">Secret: {secret}</div>}
            <Input value={code} onChange={(e)=>setCode(e.target.value)} placeholder="000000" maxLength={6} className="text-center text-2xl tracking-widest font-mono" data-testid="2fa-code-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
            <Button onClick={verify} className="bg-navy-800 hover:bg-navy-900" data-testid="2fa-verify">Verifica e attiva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function Impostazioni() {
  const { user, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [profile, setProfile] = useState({ full_name: user?.full_name || "", phone: user?.phone || "", email_signature: user?.email_signature || "" });

  useEffect(() => { api.get("/users").then(r => setUsers(r.data)); }, []);

  const saveProfile = async () => {
    const { data } = await api.patch(`/users/${user.id}`, profile);
    setUser({ ...user, ...data });
    toast.success("Profilo aggiornato");
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-heading font-bold text-navy-900">Impostazioni</h1>
        <p className="text-sm text-slate-500 mt-1">Gestisci studio, utenti e integrazioni</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profilo</TabsTrigger>
          <TabsTrigger value="security">Sicurezza</TabsTrigger>
          <TabsTrigger value="studio">Studio</TabsTrigger>
          <TabsTrigger value="users">Utenti</TabsTrigger>
          <TabsTrigger value="integrations">Integrazioni</TabsTrigger>
          <TabsTrigger value="ai">AI/LLM</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="p-6 space-y-4 max-w-2xl">
            <div className="space-y-1.5"><Label>Nome completo</Label><Input value={profile.full_name} onChange={(e)=>setProfile({...profile, full_name: e.target.value})} data-testid="profile-name" /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={profile.phone} onChange={(e)=>setProfile({...profile, phone: e.target.value})} /></div>
            <div className="space-y-1.5"><Label>Firma Email</Label><textarea rows={4} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" value={profile.email_signature} onChange={(e)=>setProfile({...profile, email_signature: e.target.value})} /></div>
            <Button onClick={saveProfile} className="bg-navy-800 hover:bg-navy-900" data-testid="save-profile-btn">Salva profilo</Button>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <TwoFASetup user={user} onChange={async () => { const { data } = await api.get("/auth/me"); setUser(data); }} />
        </TabsContent>

        <TabsContent value="studio">
          <Card className="p-6 space-y-4 max-w-2xl">
            <div className="space-y-1.5"><Label>Nome studio</Label><Input defaultValue="Studio Tozzi e Associati" /></div>
            <div className="space-y-1.5"><Label>Indirizzo</Label><Input placeholder="Via Roma 1, 00100 Roma"/></div>
            <div className="space-y-1.5"><Label>PEC studio</Label><Input placeholder="studio@pec.it"/></div>
            <div className="space-y-1.5"><Label>Prefisso protocollo</Label><Input defaultValue={String(new Date().getFullYear())} /></div>
            <div className="text-xs text-slate-400 pt-2">Le impostazioni dello studio verranno persistite tramite endpoint /api/settings.</div>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-5 py-2.5">Nome</th><th className="text-left px-5 py-2.5">Email</th><th className="text-left px-5 py-2.5">Ruolo</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-5 py-3 text-navy-900 font-medium">{u.full_name}</td>
                    <td className="px-5 py-3 text-slate-600">{u.email}</td>
                    <td className="px-5 py-3"><Badge variant="outline" className="capitalize">{u.role?.replace("_"," ")}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="space-y-5">
            <EmailInboundConfig />
            <div className="grid md:grid-cols-2 gap-5">
            <Card className="p-6 space-y-3">
              <div className="font-heading font-semibold text-navy-900">Microsoft 365</div>
              <p className="text-xs text-slate-500">Configura Azure App Registration per SharePoint, Outlook e Calendario.</p>
              <Input placeholder="Tenant ID" />
              <Input placeholder="Client ID" />
              <Input type="password" placeholder="Client Secret" />
              <Button variant="outline" className="w-full">Testa connessione</Button>
              <div className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded">Placeholder · richiede credenziali Azure</div>
            </Card>
            <Card className="p-6 space-y-3">
              <div className="font-heading font-semibold text-navy-900">PEC IMAP/SMTP</div>
              <p className="text-xs text-slate-500">Configura le caselle PEC dello studio.</p>
              <Input placeholder="Host IMAP (es. mbox.cert.legalmail.it)" />
              <Input placeholder="Utente PEC" />
              <Input type="password" placeholder="Password" />
              <Button variant="outline" className="w-full">Salva</Button>
              <div className="text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded">Placeholder · richiede credenziali provider PEC</div>
            </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai">
          <Card className="p-6 space-y-4 max-w-xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-semibold text-navy-900">Provider AI attivo</div>
                <div className="text-xs text-slate-500 mt-1">Claude Sonnet 4.5 (Anthropic) via Emergent Universal Key</div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Attivo</Badge>
            </div>
            <div className="border-t pt-4 text-xs text-slate-500 space-y-2">
              <p><strong>Modello:</strong> claude-sonnet-4-5-20250929</p>
              <p><strong>Funzionalità abilitate:</strong> riepilogo pratiche, bozze email, chatbot studio, analisi documenti.</p>
              <p className="text-amber-700 bg-amber-50 px-2 py-1 rounded">⚠ I dati delle pratiche inviati all'AI sono processati secondo la policy del provider. Per dati sensibili, considera l'uso di Ollama self-hosted.</p>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
